import { useMemo } from "react";
import Select, { components } from "react-select";
import { normalizeDropdownOptions } from "../../utils/normalizeDropdownOptions";

/* ===============================
   Checkbox Option
================================ */
const CheckboxOption = (props) => (
  <components.Option {...props}>
    <input
      type="checkbox"
      checked={props.isSelected}
      readOnly
      style={{ marginRight: 8 }}
    />
    {props.label}
  </components.Option>
);

/* ===============================
   MenuList with Select All
================================ */
const MenuList = (props) => {
  const { options, getValue, setValue } = props;
  const selectedValues = getValue() || [];

  const allSelected =
    options.length > 0 &&
    options.every((opt) =>
      selectedValues.some((sel) => sel.value === opt.value)
    );

  const toggleSelectAll = () => {
    setValue(allSelected ? [] : options, "select-option");
  };

  return (
    <components.MenuList {...props}>
      <div
        style={{
          padding: "8px 12px",
          borderBottom: "1px solid #ddd",
          cursor: "pointer",
          background: "#f7fafc",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
        onClick={toggleSelectAll}
      >
        <input type="checkbox" checked={allSelected} readOnly />
        <strong>{allSelected ? "Unselect All" : "Select All"}</strong>
      </div>

      {props.children}
    </components.MenuList>
  );
};

/* ===============================
   Styles
================================ */
const customStyles = {
  control: (base) => ({
    ...base,
    borderColor: "#CBD5E0",
    boxShadow: "none",
    minHeight: "38px",
  }),
  valueContainer: (base) => ({
    ...base,
    maxHeight: 80,
    overflowY: "auto",
    scrollbarWidth: "thin",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 150,
    overflowY: "auto",
  }),
};

/* ===============================
   Component
================================ */
export default function MultiSelectWithSelectAllCheckbox({
  options: rawOptions,
  value: rawValue,
  onChange,
  placeholder = "Select...",
  disabled,
}) {
  /* -------------------------------
     Normalize options
  -------------------------------- */
  const options = useMemo(
    () => normalizeDropdownOptions(rawOptions, "MultiSelectWithSelectAll"),
    [rawOptions]
  );

  /* -------------------------------
     Normalize value
  -------------------------------- */
  const value = useMemo(() => {
    if (!rawValue) return [];

    // string: "A,B,C"
    if (typeof rawValue === "string") {
      return rawValue
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .map((v) => ({ value: v, label: v }));
    }

    // array of strings or objects
    if (Array.isArray(rawValue)) {
      return rawValue
        .map((v) =>
          typeof v === "string"
            ? { value: v, label: v }
            : v?.value
            ? { value: v.value, label: v.label ?? v.value }
            : null
        )
        .filter(Boolean);
    }

    return [];
  }, [rawValue]);

  /* -------------------------------
     Change handler (sanitized)
  -------------------------------- */
  const handleChange = (selected) => {
    const safe = Array.isArray(selected) ? selected : [];
    onChange?.(safe.map((opt) => opt.value));
  };

  return (
    <Select
      isMulti
      isDisabled={disabled}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      options={options}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      styles={customStyles}
      components={{
        Option: CheckboxOption,
        MenuList,
      }}
      menuPortalTarget={document.body}
    />
  );
}
